import {
  useEffect, useMemo, useRef, useState,
} from "react";
import { toast } from "sonner";
import { postFile } from "@/shared/services/directUploads.service";
import {
  useBankMovementAccountsQuery,
  useBankMovementPaymentModesQuery,
  useUpdateBankMovement,
  useCreateBankMovement,
  useDeleteBankMovement,
} from "../../hooks/useAccountingQueries";
import {
  areNumbersEqual,
  extractApiErrorMessage,
  extractFileNameFromAttachmentUrl,
  fileTypeFromMime,
  isServerRejection,
  LEGACY_PAYMENT_MODES,
  normalizeSearchText,
  parseNullableNumber,
  toEditableMovementState,
} from "./utils";
import type { EditableMovementState, MovementAttachmentState } from "./utils";
import type { BankMovement } from "../../types/accounting.types";

type UseBankMovementFormParams = {
  bankMovement: BankMovement | null
  bankMovementId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onBankMovementUpdated?: (movement: BankMovement) => void
  onBankMovementDeleted?: () => void
  skipNextAutoCleanupRef: React.RefObject<boolean>
};

export const useBankMovementForm = ({
  bankMovement,
  bankMovementId,
  open,
  onOpenChange,
  onBankMovementUpdated,
  onBankMovementDeleted,
  skipNextAutoCleanupRef,
}: UseBankMovementFormParams) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [movementSnapshot, setMovementSnapshot] = useState<EditableMovementState | null>(null);
  const [movementDraft, setMovementDraft] = useState<EditableMovementState | null>(null);
  const [accountPopoverOpen, setAccountPopoverOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [paymentModePopoverOpen, setPaymentModePopoverOpen] = useState(false);
  const [paymentModeSearch, setPaymentModeSearch] = useState("");
  const [movementAttachment, setMovementAttachment] = useState<MovementAttachmentState | null>(null);
  const [pendingAttachmentSignedId, setPendingAttachmentSignedId] = useState<string | null>(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const [isDeleteMovementDialogOpen, setIsDeleteMovementDialogOpen] = useState(false);
  const isSavingRef = useRef(false);
  const hasAutoFilledAccountRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Queries & mutations
  // ---------------------------------------------------------------------------

  const { data: bankAccounts = [], isLoading: isBankAccountsLoading } = useBankMovementAccountsQuery({});
  const { data: paymentModes = [], isLoading: isPaymentModesLoading } = useBankMovementPaymentModesQuery({});
  const updateBankMovementMutation = useUpdateBankMovement(bankMovementId);
  const createBankMovementMutation = useCreateBankMovement();
  const deleteBankMovementMutation = useDeleteBankMovement();

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    hasAutoFilledAccountRef.current = false;
    if (bankMovement) {
      const editable = toEditableMovementState(bankMovement);
      setMovementSnapshot(editable);
      setMovementDraft(editable);
      setMovementAttachment(bankMovement.comptaPiece?.url
        ? {
          url: bankMovement.comptaPiece.url,
          fileName: bankMovement.fileName
            ?? extractFileNameFromAttachmentUrl(bankMovement.comptaPiece.url)
            ?? null,
          fileType: bankMovement.fileType ?? null,
        }
        : null);
      setPendingAttachmentSignedId(null);
    }
    else {
      setMovementSnapshot(null);
      setMovementDraft(null);
      setMovementAttachment(null);
      setPendingAttachmentSignedId(null);
    }
  }, [bankMovement]);

  useEffect(() => {
    if (hasAutoFilledAccountRef.current) return;
    if (!movementDraft) return;
    if (movementDraft.accountId) return;
    if (bankAccounts.length === 0) return;
    hasAutoFilledAccountRef.current = true;
    setMovementDraft((previous) => {
      if (!previous || previous.accountId) return previous;
      return { ...previous, accountId: bankAccounts[0].id };
    });
  }, [bankAccounts, movementDraft]);

  useEffect(() => {
    if (!open) {
      setAccountPopoverOpen(false);
      setPaymentModePopoverOpen(false);
    }
  }, [open]);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const selectedAccount = useMemo(() => {
    const accountId = movementDraft?.accountId || bankMovement?.accountId;
    if (!accountId) return null;
    return bankAccounts.find((account) => account.id === accountId) ?? null;
  }, [bankAccounts, bankMovement?.accountId, movementDraft?.accountId]);

  const selectedAccountBudgetType = useMemo(() => {
    const bt = selectedAccount?.budgetType?.trim().toUpperCase();
    return bt === "AEP" ? "AEP" : "ASC";
  }, [selectedAccount?.budgetType]);

  const isCaisseAccount = selectedAccount?.accountType === "Caisse";

  const filteredPaymentModes = useMemo(() => {
    const query = normalizeSearchText(paymentModeSearch);
    // Une caisse ne manipule que des espèces : seul « Espèces » est proposé
    // (« Remise d'espèces » couvre la remise en banque côté Bancaire). Cf. ADM-656.
    const baseModes = isCaisseAccount
      ? ["Espèces"]
      : (() => {
        const legacyModes = LEGACY_PAYMENT_MODES.filter(Boolean);
        const apiModes = paymentModes.filter(Boolean);
        const combinedModes = [...legacyModes];
        apiModes.forEach((mode) => {
          if (!combinedModes.includes(mode)) combinedModes.push(mode);
        });
        return combinedModes.filter((mode) => mode !== "Espèces");
      })();
    if (!query) return baseModes;
    return baseModes.filter((mode) => normalizeSearchText(mode).includes(query));
  }, [paymentModeSearch, paymentModes, isCaisseAccount]);

  const isMovementDirty = useMemo(() => {
    if (!movementSnapshot || !movementDraft) return false;
    return (
      movementDraft.date !== movementSnapshot.date
      || movementDraft.depositDate !== movementSnapshot.depositDate
      || movementDraft.issueDate !== movementSnapshot.issueDate
      || movementDraft.label !== movementSnapshot.label
      || movementDraft.accountId !== movementSnapshot.accountId
      || movementDraft.paymentMode !== movementSnapshot.paymentMode
      || movementDraft.remittanceNumber !== movementSnapshot.remittanceNumber
      || movementDraft.paymentNumber !== movementSnapshot.paymentNumber
      || !areNumbersEqual(parseNullableNumber(movementDraft.debit), parseNullableNumber(movementSnapshot.debit))
      || !areNumbersEqual(parseNullableNumber(movementDraft.credit), parseNullableNumber(movementSnapshot.credit))
    );
  }, [movementDraft, movementSnapshot]);

  // ---------------------------------------------------------------------------
  // Busy flags
  // ---------------------------------------------------------------------------

  const isMovementBusy = updateBankMovementMutation.isPending || createBankMovementMutation.isPending;
  const isDeleteMovementBusy = deleteBankMovementMutation.isPending;
  const isPersistedMovement = Boolean(bankMovementId);
  const canCreateMovement = Boolean(movementDraft?.accountId && movementDraft?.date);
  const canSaveMovement = isPersistedMovement ? isMovementDirty : canCreateMovement;
  const isBridgeMovement = bankMovement?.source?.toLowerCase().includes("bridge") ?? false;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const setMovementField = (field: keyof EditableMovementState, value: string) => {
    setMovementDraft((previous) => {
      if (!previous) return previous;
      return { ...previous, [field]: value };
    });
  };

  const handleSaveMovement = async (options?: { includeAttachment?: boolean, attachmentSignedId?: string | null }) => {
    if (!bankMovement || !movementDraft) return;
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    const includeAttachment = options?.includeAttachment ?? (pendingAttachmentSignedId !== null);

    try {
      const payload = {
        date: movementDraft.date || null,
        depositDate: movementDraft.depositDate || null,
        issueDate: movementDraft.issueDate || null,
        label: movementDraft.label || null,
        accountId: movementDraft.accountId || null,
        paymentMode: movementDraft.paymentMode || null,
        remittanceNumber: movementDraft.remittanceNumber || null,
        paymentNumber: movementDraft.paymentNumber || null,
        debit: parseNullableNumber(movementDraft.debit),
        credit: parseNullableNumber(movementDraft.credit),
        ...(includeAttachment
          ? { comptaPieceSignedId: options?.attachmentSignedId ?? pendingAttachmentSignedId }
          : {}),
      };
      const updatedMovement = bankMovementId
        ? await updateBankMovementMutation.mutateAsync(payload)
        : await createBankMovementMutation.mutateAsync(payload);

      if (updatedMovement) {
        const nextState = toEditableMovementState(updatedMovement);
        setMovementSnapshot(nextState);
        setMovementDraft(nextState);
        setPendingAttachmentSignedId(null);
        onBankMovementUpdated?.(updatedMovement);
      }
      else if (bankMovementId) {
        setMovementSnapshot(movementDraft);
      }
      else {
        toast.error("Le mouvement a été créé mais la réponse est incomplète. Veuillez rafraîchir la page.");
        return;
      }

      toast.success(bankMovementId ? "Mouvement bancaire mis à jour" : "Mouvement bancaire créé");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
      // Refus du serveur : rien n'est enregistré, mais le formulaire affichait
      // encore la valeur refusée comme si elle l'était. On revient à la dernière
      // version connue de la base. À la création il n'y en a pas : la saisie
      // reste à l'écran, sinon on la perdrait entièrement.
      if (isServerRejection(error) && bankMovementId && movementSnapshot) {
        setMovementDraft(movementSnapshot);
      }
    }
    finally {
      isSavingRef.current = false;
    }
  };

  const handleUploadAttachment = async (file: File) => {
    if (!movementDraft) return;
    setIsUploadingAttachment(true);
    try {
      const uploaded = await postFile(file);
      const nextAttachment: MovementAttachmentState = {
        url: uploaded.publicUrl,
        fileName: file.name,
        fileType: fileTypeFromMime(file.type),
      };
      setMovementAttachment(nextAttachment);

      if (!bankMovementId) {
        setPendingAttachmentSignedId(uploaded.signedId);
        return;
      }

      await handleSaveMovement({ includeAttachment: true, attachmentSignedId: uploaded.signedId });
      toast.success("Pièce jointe enregistrée");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async () => {
    if (!movementDraft || !movementAttachment) return;
    setIsDeletingAttachment(true);
    try {
      if (!bankMovementId) {
        setMovementAttachment(null);
        setPendingAttachmentSignedId(null);
        return;
      }
      await handleSaveMovement({ includeAttachment: true, attachmentSignedId: null });
      setMovementAttachment(null);
      toast.success("Pièce jointe supprimée");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setIsDeletingAttachment(false);
    }
  };

  const handleDeleteMovement = async () => {
    if (!bankMovementId) return;
    const toastId = toast.loading("Suppression du mouvement...");
    try {
      await deleteBankMovementMutation.mutateAsync(bankMovementId);
      setIsDeleteMovementDialogOpen(false);
      toast.success("Mouvement supprimé", { id: toastId });
      onBankMovementDeleted?.();
      skipNextAutoCleanupRef.current = true;
      onOpenChange(false);
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
    }
  };

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    movementDraft,
    setMovementField,
    accountPopoverOpen,
    setAccountPopoverOpen,
    accountSearch,
    setAccountSearch,
    bankAccounts,
    isBankAccountsLoading,
    selectedAccount,
    selectedAccountBudgetType,
    paymentModePopoverOpen,
    setPaymentModePopoverOpen,
    paymentModeSearch,
    setPaymentModeSearch,
    filteredPaymentModes,
    isPaymentModesLoading,
    movementAttachment,
    attachmentInputRef,
    isUploadingAttachment,
    isDeletingAttachment,
    isMovementBusy,
    isPersistedMovement,
    canSaveMovement,
    isDeleteMovementBusy,
    isBridgeMovement,
    isDeleteMovementDialogOpen,
    setIsDeleteMovementDialogOpen,
    handleSaveMovement: () => void handleSaveMovement(),
    handleUploadAttachment: (file: File) => void handleUploadAttachment(file),
    handleDeleteAttachment: () => void handleDeleteAttachment(),
    handleDeleteMovement: () => void handleDeleteMovement(),
  };
};
